import { QueryStringInput } from '@hello-aria/query-string'
import { Company } from '../models/companyModel'
import { RequestResponse, requestService } from './requestServices'
import {
    CreateSubscription,
    CreateSubscriptionGateway,
    DeleteSubscription,
    EditSubscription,
    EditSubscriptionGateway,
    Subscription,
    SubscriptionGateway
} from '../models/subscriptionModels'
import { resolveApiUrl } from '../utils/requestsUtils'
import { isEmpty, isNil } from 'lodash'
import { trackEvent } from '../utils/analyticsUtils'

export const listSubscriptionsService = async (
    companyId: Company['id'],
    query?: QueryStringInput
): Promise<RequestResponse<Subscription[]> | undefined> => {
    const url = resolveApiUrl(`subscriptions.list`, {
        variables: { companyId },
        query
    })

    const response = await requestService<SubscriptionGateway[]>(url)
    return response
}

export const getSubscriptionByIdService = async (
    companyId: string | undefined,
    subscriptionId: string | undefined
): Promise<RequestResponse<Subscription> | undefined> => {
    if (isNil(companyId) || isNil(subscriptionId)) return
    const url = resolveApiUrl('subscriptions.get', {
        variables: { companyId, subscriptionId }
    })
    return requestService<SubscriptionGateway>(url)
}

export const createSubscriptionService = async (
    companyId: Company['id'],
    payload: CreateSubscription
): Promise<RequestResponse<Subscription> | undefined> => {
    const url = resolveApiUrl('subscriptions.create', { variables: { companyId } })
    const { options } = payload

    let response
    if (isEmpty(options.secret)) {
        const formattedPayload: CreateSubscription = { ...payload, options: { endpoint: options.endpoint } }
        response = await requestService<CreateSubscriptionGateway>(url, {
            method: 'POST',
            data: formattedPayload
        })
    } else {
        response = await requestService<CreateSubscriptionGateway>(url, { method: 'POST', data: payload })
    }

    trackEvent('Create_Subscription')

    return response
}

export const editSubscriptionService = async (
    companyId: Company['id'],
    payload: EditSubscription
): Promise<RequestResponse<Subscription> | undefined> => {
    const { subscriptionId, options } = payload
    const url = resolveApiUrl('subscriptions.edit', { variables: { companyId, subscriptionId } })

    const secret = isEmpty(options.secret) ? undefined : options.secret
    const response = await requestService<EditSubscriptionGateway>(url, {
        method: 'PATCH',
        data: { options: { ...options, secret } }
    })

    trackEvent('Update_Subscription')

    return response
}

export const deleteSubscriptionService = async (
    companyId: Company['id'],
    payload: DeleteSubscription
): Promise<RequestResponse<Subscription> | undefined> => {
    const url = resolveApiUrl('subscriptions.delete', {
        variables: { companyId, subscriptionId: payload.subscriptionId }
    })
    const response = await requestService<SubscriptionGateway>(url, { method: 'DELETE' })

    trackEvent('Delete_Subscription')

    return response
}
